import React, { lazy } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFiles,
  faUsers,
  faRocket,
  faFilters,
  faServer,
  faBriefcase,
  faListCheck,
  faTable,
  faWrench,
  faInbox,
  faComments,
  faBomb,
  faChartLine,
  faGlobe,
  faFlask,
  faChartPie,
  faBolt,
  faDiagramSubtask,
  faShieldHeart,
  faBook,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { faFolderOpen, faRobot } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { MODULE_REGISTRY, NAV_ONLY_APPS, type ModuleDefinition } from "@/config/modules";
import { openArtifactById } from "@/components/artifacts/openArtifactDestination";
import { canAccessGuardians } from "@/utils/guardiansAccess";
import { canAccessAniPlatformAdminOps } from "@/utils/curoOpsAccess";
import { canAccessCodex } from "@/utils/codexAccess";
import { canAccessMissionKitPlatform } from "@/utils/missionKitAccess";
import { registerAppRegistry } from "./appRegistryAccess";
import type { ModulePermissions } from "@/services/api/user/types";

// Eager imports (lightweight or critical path)
const Dashboard = lazy(() => import("@/routes/dashboard"));
const WorkerDirectory = lazy(() => import("@/components/pods/WorkerDirectory"));
const WorkerChatLayout = lazy(() => import("@/components/pods/WorkerChatLayout"));
const WorkerRoute = lazy(() => import("@/routes/worker"));
import {
  WorkerIdContext,
  WorkerConversationIdContext,
} from "@/routes/workerContexts";
import PodDirectory from "@/components/pods/PodDirectory";
import { PodRoute } from "@/routes/pod";
import { PodInstance } from "@/components/pods/PodInstance";
import { MissionsListRoute } from "@/routes/missions";
import { ClientsListRoute } from "@/routes/clients";
import { ClientDetailRoute } from "@/routes/client";
import { LeadsListRoute } from "@/routes/leads";
import { LeadDetailRoute } from "@/routes/lead";
import { MissionDetailRoute } from "@/routes/mission";
import WorkerCreateRoute from "@/routes/worker-create";
import PortfoliosWindow from "./PortfoliosWindow";
import { DashboardProvider } from "@/context/DashboardContext";
import { Minesweeper } from "@/components/games/Minesweeper";
import { ReadableContentWindow } from "@/components/ui/readable-content";
import { CsvTablePreviewWindow } from "@/components/ui/csv-table-preview";
import { LoadingPlaceholder } from "@/components/ui/loading-placeholder";
import { getDocument } from "@/services/api/documents";
import type { Document } from "@/services/api/documents/types";
import type { DocumentContent } from "@/types/documents";
import { useSetWindowTitle } from "./useWindow";
import "./osAppChunkLoaders";
import { OsChecklistsWindowContent } from "./OsChecklistsWindowContent";
import { OsAgentReviewWindowContent } from "./OsAgentReviewWindowContent";
import { OsProgenyReviewWindowBridge } from "./OsProgenyReviewWindowBridge";
import { GuardiansApp } from "@/components/guardians";
import { GuardiansConversationWindow } from "@/components/guardians/conversation/GuardiansConversationWindow";
import { CaseCheckingView } from "@/components/case-checking";
import { DisclosuresView } from "@/components/disclosures";

const CodexPage = lazy(() =>
  import("@/components/codex/CodexPage").then((m) => ({ default: m.CodexPage })),
);
const CodexMissionPage = lazy(() =>
  import("@/components/codex/CodexMissionPage").then((m) => ({ default: m.CodexMissionPage })),
);
const CodexTaskPage = lazy(() =>
  import("@/components/codex/CodexTaskPage").then((m) => ({ default: m.CodexTaskPage })),
);
const CodexAgentPage = lazy(() =>
  import("@/components/codex/CodexAgentPage").then((m) => ({ default: m.CodexAgentPage })),
);
function OsEmbeddedRouteFrame({
  basePath,
  params,
}: {
  basePath: string;
  params: Record<string, string>;
}) {
  const src = React.useMemo(() => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        search.set(key, value);
      }
    });
    const query = search.toString();
    return query ? `${basePath}?${query}` : basePath;
  }, [basePath, params]);

  return (
    <iframe
      title={basePath}
      src={src}
      className="h-full w-full min-h-0 border-0 bg-white dark:bg-slate-900"
    />
  );
}

// Lazy imports (heavy routes - loaded on demand)
const Reports = lazy(() => import("@/routes/reports"));
const Meetings = lazy(() => import("@/routes/meetings"));
const MeetingWindow = lazy(() =>
  import("@/components/meetings/MeetingWindow").then((m) => ({
    default: m.MeetingWindow,
  }))
);
const ScreenerWindowContent = lazy(() => import("@/routes/screener").then(m => ({ default: m.ScreenerWindowContent })));
const PlaygroundWindowContent = lazy(() => import("@/routes/playground").then(m => ({ default: m.PlaygroundWindowContent })));
const Documents = lazy(() => import("@/routes/documents"));
const Ops = lazy(() => import("@/routes/ops"));
const Settings = lazy(() => import("@/routes/settings"));
const OpsChecker = lazy(() => import("@/routes/ops.checker"));
const OpsSystemJobs = lazy(() => import("@/routes/ops.system-jobs"));
const OpsTaskManager = lazy(() => import("@/routes/ops.task-manager"));
const OpsMissionControlOps = lazy(() => import("@/routes/ops.mission-control-ops"));
const OpsEtlRuns = lazy(() => import("@/routes/ops.etl-runs"));
const OpsDbVersions = lazy(() => import("@/routes/ops.db-versions"));
const OpsWorkerTemplates = lazy(() => import("@/routes/ops.worker-templates"));
const OpsEtlAudit = lazy(() => import("@/routes/ops.etl-audit"));
const OpsSnapshots = lazy(() => import("@/routes/ops.snapshots"));
const OpsAccessPermissions = lazy(() => import("@/routes/ops.access-permissions"));
const OpsRefinitiv = lazy(() => import("@/routes/ops.refinitiv"));
const OpsMsci = lazy(() => import("@/routes/ops.msci"));
const OpsSeccl = lazy(() => import("@/routes/ops.seccl"));
const OpsCacheManagement = lazy(() => import("@/routes/ops.cache-management"));
const OpsDataSentinel = lazy(() => import("@/routes/ops.data-sentinel"));
const OpsTemporalRagPoc = lazy(() => import("@/routes/ops.temporal-rag"));
const OpsDataGathering = lazy(() => import("@/routes/ops.data-gathering"));
const DgMonitoringTaskWindow = lazy(() =>
  import("@/components/ops/data-gathering/DgMonitoringTaskWindow").then((m) => ({
    default: m.DgMonitoringTaskWindow,
  })),
);
const DataGatheringCaseWindow = lazy(() =>
  import("@/components/ops/data-gathering/DataGatheringCaseWindow").then((m) => ({
    default: m.DataGatheringCaseWindow,
  })),
);
const OpsFeatureFlags = lazy(() => import("@/routes/ops.feature-flags"));
const OpsExternalData = lazy(() => import("@/routes/ops.external-data"));
const OpsNewsIngest = lazy(() => import("@/routes/ops.free-news-ingest"));
const OpsAutomaticIngestions = lazy(
  () => import("@/routes/ops.automatic-ingestions"),
);
const OpsAutomaticIngestionsDailyIhf = lazy(
  () => import("@/routes/ops.automatic-ingestions.daily-ihf"),
);
const OpsAutomaticIngestionsCashFundRec = lazy(
  () => import("@/routes/ops.automatic-ingestions.daily-ihf-cash-fund-rec"),
);
const OpsMagicUpload = lazy(() => import("@/routes/ops.magic-upload"));
const OpsAgentTesting = lazy(() => import("@/routes/ops.agent-testing"));
const OpsAgentTestingConfigure = lazy(() => import("@/routes/ops.agent-testing.configure"));
const OpsAgentTestingAbTest = lazy(
  () => import("@/routes/ops.agent-testing.ab-tests.$id"),
);
const OsAceTaskReviewWindowContent = lazy(() =>
  import("./OsAceTaskReviewWindowContent").then((m) => ({
    default: m.OsAceTaskReviewWindowContent,
  })),
);
const OpsCuro = lazy(() => import("@/routes/ops.curo"));
const OpsMeetingConnectors = lazy(() => import("@/routes/ops.meeting-connectors"));
const OpsProgeny = lazy(() => import("@/routes/ops.progeny"));
const CaseCheckingOpsApp = lazy(() =>
  import("@/components/ops/case-checking-ops/CaseCheckingOpsApp").then((m) => ({
    default: m.CaseCheckingOpsApp,
  })),
);
const OpsProgenyFileChecking = lazy(() => import("@/routes/ops.progeny-file-checking"));
const OpsProgenyAdvisor = lazy(() => import("@/routes/ops.progeny-advisor"));
const OpsProgenyWebhooks = lazy(() => import("@/routes/ops.progeny.webhooks"));
const OpsProgenyProcessing = lazy(() => import("@/routes/ops.progeny.processing"));
const OpsProgenySamTesting = lazy(() => import("@/routes/ops.progeny.sam-testing"));
const OpsGuardiansTickets = lazy(() =>
  import("@/components/ops/guardians/tickets/TicketsPage").then((m) => ({
    default: m.TicketsPage,
  }))
);
const OpsTriggers = lazy(() =>
  import("@/components/ops/triggers/TriggersHub").then((m) => ({
    default: m.TriggersHub,
  }))
);
const ScenarioTrackerWindow = lazy(() =>
  import("@/components/scenarios/Scenarios").then((m) => ({ default: m.Scenarios }))
);
const ScenarioMetricDetailWindow = lazy(() =>
  import("@/components/scenarios/ScenarioMetricDetailWindow").then((m) => ({
    default: m.ScenarioMetricDetailWindow,
  }))
);
const ScenarioArticleDetailWindow = lazy(() =>
  import("@/components/scenarios/ScenarioArticleDetailWindow").then((m) => ({
    default: m.ScenarioArticleDetailWindow,
  }))
);
const ScenariosAnalysis = lazy(() =>
  import("@/components/scenarios/Scenarios").then((m) => ({ default: m.Scenarios })),
);
const WhatIf = lazy(() => import("@/routes/what-if"));
const WhatIfPortfolioWindowContent = lazy(() => 
  import("@/routes/what-if.portfolio.$portfolioId").then(m => ({ 
    default: m.WhatIfPortfolioWindowContent 
  }))
);
const WhatIfScenarioWindowContent = lazy(() =>
  import("@/routes/what-if.portfolio.$portfolioId.scenario.$scenarioId").then(
    (m) => ({
      default: m.WhatIfScenarioWindowContent,
    }),
  ),
);
const CashFlow = lazy(() => import("@/routes/cash-flow"));
const AddProductsApp = lazy(() =>
  import("@/components/add-products/AddProductsApp").then((m) => ({
    default: m.AddProductsApp,
  })),
);
const FundProcessorRoute = lazy(() => import("@/routes/fund-processor"));
const EquityProcessorRoute = lazy(() => import("@/routes/equity-processor"));
const BondProcessorRoute = lazy(() => import("@/routes/bond-processor"));
const CustomProductsRoute = lazy(() => import("@/routes/custom-products"));
const TradeBlotter = lazy(() => import("@/routes/trade-blotter"));
const PortfolioBuilder = lazy(() => import("@/routes/portfolio-builder"));
const PortfolioDuplicateWindow = lazy(() =>
  import("@/components/portfolio-builder/PortfolioDuplicateWindow").then((m) => ({
    default: m.PortfolioDuplicateWindow,
  })),
);
const DataHub = lazy(() => import("@/routes/data-hub"));
const BenchmarkBuilder = lazy(() => import("@/routes/benchmark-builder"));
const MonteCarlo = lazy(() => import("@/components/monte-carlo").then(m => ({ default: m.MonteCarlo })));
const CMAs = lazy(() => import("@/components/cmas").then(m => ({ default: m.CMAs })));
const Optimiser = lazy(() => import("@/components/optimiser").then(m => ({ default: m.Optimiser })));
const FactorAnalysis = lazy(() => import("@/components/factor-analysis").then(m => ({ default: m.FactorAnalysis })));
const PortfolioLab = lazy(() => import("@/components/portfolio-lab/PortfolioLab").then(m => ({ default: m.PortfolioLab })));
const AdvisorDashboard = lazy(() => import("@/components/meridian/AdvisorDashboard").then(m => ({ default: m.AdvisorDashboard })));
const Home = lazy(() =>
  import("@/components/home/Home").then((m) => ({ default: m.Home })),
);
const PulseDashboard = lazy(() => import("@/components/pulse/PulseDashboard").then(m => ({ default: m.PulseDashboard })));
const RebalancingContainer = lazy(() => import("@/components/rebalancing/RebalancingContainer").then(m => ({ default: m.RebalancingContainer })));
const TWRContainer = lazy(() => import("@/components/twr").then(m => ({ default: m.TWRContainer })));
const IRRContainer = lazy(() => import("@/components/irr").then(m => ({ default: m.IRRContainer })));
const CalculatorsWindowContent = lazy(() =>
  import("@/routes/calculators").then((m) => ({ default: m.CalculatorsWindowContent })),
);
const CaseCheckingRules = lazy(() => import("@/routes/case-checking.rules"));
const FcaApplicantPortal = lazy(() =>
  import("@/components/fca-portal/FcaApplicantPortal").then((m) => ({ default: m.FcaApplicantPortal })),
);
const Portfolio = lazy(() => import("@/components/portfolio/Portfolio").then(m => ({ default: m.Portfolio })));
const Product = lazy(() => import("@/components/product/Product").then(m => ({ default: m.Product })));
const Inbox = lazy(() => import("@/components/inbox/Inbox").then(m => ({ default: m.Inbox })));
const MissionControl = lazy(() => import("@/components/mission-control/MissionControl").then(m => ({ default: m.MissionControl })));
const SpaceInstance = lazy(() => import("@/components/pods/SpaceInstance").then(m => ({ default: m.SpaceInstance })));
const SpaceTemplate = lazy(() => import("@/components/pods/SpaceTemplate").then(m => ({ default: m.SpaceTemplate })));
const DocumentEditorDrawer = lazy(() => import("@/components/documents/DocumentEditorDrawer").then(m => ({ default: m.DocumentEditorDrawer })));
const ArtifactsApp = lazy(() => import("@/components/artifacts/ArtifactsApp").then(m => ({ default: m.ArtifactsApp })));
const ArtifactWindow = lazy(() => import("@/components/artifacts/ArtifactWindow").then(m => ({ default: m.ArtifactWindow })));
const DocumentTemplateWindow = lazy(() => import("@/components/artifacts/DocumentTemplateWindow").then(m => ({ default: m.DocumentTemplateWindow })));
const FormSchemaWindow = lazy(() => import("@/components/artifacts/FormSchemaWindow").then(m => ({ default: m.FormSchemaWindow })));
const ChecklistSchemaWindow = lazy(() =>
  import("@/components/artifacts/ChecklistSchemaWindow").then((m) => ({
    default: m.ChecklistSchemaWindow,
  })),
);
const TaskReviewWorkspace = lazy(() =>
  import("@/components/review/TaskReviewWorkspace").then((m) => ({
    default: m.TaskReviewWorkspace,
  })),
);
const ReportBuilderWindowContent = lazy(() =>
  import("@/routes/report-builder").then((m) => ({
    default: m.ReportBuilderWindowContent,
  })),
);
const MissionKitHub = lazy(() =>
  import("@/components/mission-kit/MissionKitHub").then((m) => ({
    default: m.MissionKitHub,
  })),
);
const MissionBlueprintPage = lazy(() =>
  import("@/components/mission-kit/blueprints/MissionBlueprintPage").then((m) => ({
    default: m.MissionBlueprintPage,
  })),
);
const MissionKitRunbookPage = lazy(() =>
  import("@/components/mission-kit/kits/MissionKitRunbookPage").then((m) => ({
    default: m.MissionKitRunbookPage,
  })),
);

function HomeWindowContent() {
  return (
    <div
      className="h-full min-h-0 overflow-y-auto overscroll-y-contain"
      data-testid="home-window-scroll"
    >
      <React.Suspense fallback={null}>
        <Home />
      </React.Suspense>
    </div>
  );
}

/**
 * Wrapper for Report Builder to simulate URL search params from window params.
 * Window title is managed internally via useWindowTitle hook in ReportBuilderWindowContent.
 */
function ReportBuilderWindow({ params }: { params: Record<string, string> }) {
  const shouldOpenWizard = params.new === "true";

  // Trigger wizard opening via global event after component mounts
  React.useEffect(() => {
    if (shouldOpenWizard) {
      // Dispatch event to open the wizard
      window.dispatchEvent(new Event("report-builder:open-wizard"));
    }
  }, [shouldOpenWizard]);

  // Create a mock setSearchParams that does nothing since we control params via the window system
  const mockSetSearchParams = React.useCallback(
    (
      _params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
    ) => {
      // No-op: in window mode, params are controlled by the window system
    },
    [],
  );

  // Convert params to URLSearchParams (but don't include 'new' since we handle it via event)
  const searchParams = React.useMemo(() => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (key !== "new") {
        // Exclude 'new' param since we handle it via event
        sp.set(key, value);
      }
    });
    return sp;
  }, [params]);

  return (
    <React.Suspense
      fallback={
        <div className="flex h-full items-center justify-center text-sm text-slate-500">
          Loading report builder…
        </div>
      }
    >
      <ReportBuilderWindowContent
        searchParams={searchParams}
        setSearchParams={mockSetSearchParams}
      />
    </React.Suspense>
  );
}

/**
 * Provides workerId via context so WorkerRoute can read it without relying on
 * URL params. This avoids nested RouterProvider issues — WorkerRoute renders
 * inside the existing app router, reads workerId from context, and activates
 * isStandaloneMode to suppress URL mutations.
 */
function WorkerWindow({
  workerId,
  conversationId,
  isActive,
}: {
  workerId: string;
  conversationId?: string;
  isActive?: boolean;
}) {
  return (
    <WorkerIdContext.Provider value={workerId}>
      <WorkerConversationIdContext.Provider value={conversationId}>
        <WorkerRoute isActive={isActive} />
      </WorkerConversationIdContext.Provider>
    </WorkerIdContext.Provider>
  );
}

function resolveResourceWindowId(
  params: Record<string, string>,
  aliases: readonly string[],
): string {
  for (const key of aliases) {
    const value = String(params[key] ?? "").trim();
    if (value) {
      return value;
    }
  }
  return "";
}

/**
 * Wrapper for Portfolio component to display in a window
 */
function PortfolioWindow({ params }: { params: Record<string, string> }) {
  const portfolioId = resolveResourceWindowId(params, ["id", "portfolioId", "portfolio_id"]);
  return (
    <DashboardProvider>
      <Portfolio id={portfolioId} skipUrlNavigation={true} />
    </DashboardProvider>
  );
}

/**
 * Wrapper for Product component to display in a window
 */
function ProductWindow({ params }: { params: Record<string, string> }) {
  const productId = resolveResourceWindowId(params, ["id", "productId", "product_id"]);
  return <Product id={productId} skipUrlNavigation={true} />;
}

function toDocumentContent(body: Document["body"]): DocumentContent | undefined {
  if (!body || body.type !== "doc") return undefined;
  return body as DocumentContent;
}

/**
 * Wrapper for Document editor to display in a window
 */
function DocumentWindow({ documentId }: { documentId: string }) {
  const [document, setDocument] = React.useState<Document | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const setWindowTitle = useSetWindowTitle();

  React.useEffect(() => {
    async function fetchDocument() {
      try {
        const doc = await getDocument(documentId);
        setDocument(doc);
        setLoading(false);

        const title =
          (doc.display_name && String(doc.display_name).trim()) ||
          (doc.filename && String(doc.filename).trim()) ||
          (doc.original_filename && String(doc.original_filename).trim()) ||
          "Document";
        setWindowTitle(title);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to load document"),
        );
        setLoading(false);
      }
    }
    fetchDocument();
  }, [documentId, setWindowTitle]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500 dark:text-gray-400">
          Loading document...
        </div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">Failed to load document</div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <DocumentEditorDrawer
        isOpen={true}
        onClose={() => {}}
        document={document}
        initialContent={toDocumentContent(document.body)}
        onSaveComplete={() => {}}
        isWindowMode={true}
      />
    </div>
  );
}

export interface AppDefinition {
  id: string;
  label: string;
  icon: React.ReactNode;
  /** Visual theme for app icon */
  colorTheme?: {
    type:
      | "gradient"
      | "solid"
      | "glass"
      | "bordered"
      | "radial"
      | "conic"
      | "mesh";
    colors: string; // Tailwind classes
    iconColor: string; // Icon color for gradient mode
    coloredIconColor?: string; // Icon color for colored mode (dark bg)
    shadow?: string;
    /** Icon style - 'light' uses white icons, 'colored' uses colored icons */
    iconStyle?: "light" | "colored";
  };
  defaultSize?: { width: number; height: number };
  minSize?: { width: number; height: number };
  /** Whether the OS window can be resized or maximized by the user. */
  isResizable?: boolean;
  /** Whether this app appears in the dock */
  showInDock?: boolean;
  /** Module name for permission checking (maps to module permissions) */
  module?: string;
  /**
   * When true, this app always opens in a small floating window at its
   * ``defaultSize`` instead of inheriting maximised state from the focused
   * window. Use for utility / dialog-style apps (e.g. minesweeper).
   */
  openWindowed?: boolean;
  /** Whether this app should reuse an existing window instead of opening multiple instances. */
  isSingleton?: boolean;
  /** When set, windows with the same non-null key are the same window; null always opens anew. */
  windowKey?: (params: Record<string, string>) => string | null;
  /** When set, dedupe only considers windows in the active workspace. */
  windowScope?: "workspace";
  /** When focusing an existing window, demote other maximized windows in the workspace. */
  demoteMaximizedOnFocus?: boolean;
  render: (
    params: Record<string, string>,
    openWindow: (config: OpenWindowParams) => void,
    isActive?: boolean,
  ) => React.ReactNode;
}

/** Minimal params needed to open a window — no pre-rendered JSX */
export interface OpenWindowParams {
  appId: string;
  title: string;
  params?: Record<string, string>;
  initialSize?: { width: number; height: number };
}

const APP_ICON_CLASS =
  "text-xl [--fa-primary-opacity:1] [--fa-secondary-opacity:0.4]";

/**
 * Creates an AppDefinition from a module definition.
 * This helper derives app properties from the centralized MODULE_REGISTRY,
 * ensuring consistency between classic and OS modes.
 *
 * @param moduleId - The module ID in the registry (underscore format)
 * @param renderFn - The render function for the app
 * @returns A partial AppDefinition with all derivable properties
 */
function createAppFromModule(
  moduleId: string,
  renderFn: AppDefinition["render"]
): AppDefinition {
  const module = MODULE_REGISTRY[moduleId];
  if (!module) {
    throw new Error(`Module "${moduleId}" not found in MODULE_REGISTRY`);
  }

  const appId = module.osAppId || module.routePath.replace(/^\//, "");
  const icon = module.osIcon || module.icon;

  return {
    id: appId,
    label: module.label,
    icon: <FontAwesomeIcon icon={icon} className={APP_ICON_CLASS} />,
    colorTheme: module.colorTheme as AppDefinition["colorTheme"],
    showInDock: module.showInDock,
    module: module.id,
    defaultSize: module.defaultSize,
    minSize: module.minSize,
    render: renderFn,
  };
}

type ScenarioTrackerTabId =
  | "exec-summary"
  | "news-flow"
  | "master-analysis"
  | "summary"
  | "deep-dive"
  | "metric-projections";

const SCENARIO_TRACKER_COLOR_THEMES: Record<
  ScenarioTrackerTabId,
  NonNullable<AppDefinition["colorTheme"]>
> = {
  "exec-summary": {
    type: "gradient",
    colors: "from-emerald-500 via-green-600 to-teal-700",
    iconColor: "text-white",
    coloredIconColor: "text-emerald-300",
    shadow: "shadow-lg",
  },
  "news-flow": {
    type: "gradient",
    colors: "from-sky-500 via-blue-600 to-indigo-700",
    iconColor: "text-white",
    coloredIconColor: "text-sky-300",
    shadow: "shadow-lg",
  },
  "master-analysis": {
    type: "gradient",
    colors: "from-violet-500 via-purple-600 to-fuchsia-700",
    iconColor: "text-white",
    coloredIconColor: "text-violet-300",
    shadow: "shadow-lg",
  },
  summary: {
    type: "gradient",
    colors: "from-amber-400 via-orange-500 to-rose-600",
    iconColor: "text-white",
    coloredIconColor: "text-amber-300",
    shadow: "shadow-lg",
  },
  "deep-dive": {
    type: "gradient",
    colors: "from-rose-500 via-red-600 to-orange-700",
    iconColor: "text-white",
    coloredIconColor: "text-rose-300",
    shadow: "shadow-lg",
  },
  "metric-projections": {
    type: "gradient",
    colors: "from-cyan-500 via-teal-500 to-emerald-600",
    iconColor: "text-white",
    coloredIconColor: "text-cyan-300",
    shadow: "shadow-lg",
  },
};

function createScenarioTrackerTabApp(
  id: string,
  label: string,
  tab: ScenarioTrackerTabId,
  icon: ModuleDefinition["icon"]
): AppDefinition {
  const module = MODULE_REGISTRY.scenario_tracker;

  return {
    id,
    label,
    icon: <FontAwesomeIcon icon={icon} className="text-xl" />,
    colorTheme: SCENARIO_TRACKER_COLOR_THEMES[tab],
    showInDock: true,
    module: module.id,
    defaultSize: module.defaultSize,
    minSize: module.minSize,
    render: () => <ScenarioTrackerWindow initialTab={tab} fixedTab />,
  };
}

export const APP_REGISTRY: Record<string, AppDefinition> = {
  // ============================================================================
  // Special Apps (not derived from MODULE_REGISTRY)
  // ============================================================================

  // Inbox is a utility app without module permission
  inbox: {
    id: "inbox",
    label: "Inbox",
    icon: <FontAwesomeIcon icon={faInbox} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-sky-600 via-blue-600 to-blue-700",
      iconColor: "text-white",
      coloredIconColor: "text-blue-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 1000, height: 700 },
    render: () => <Inbox />,
  },

  // Mission Control - Central hub for workflow monitoring
  "mission-control": {
    ...createAppFromModule("mission_control", () => <MissionControl />),
    windowScope: "workspace",
    windowKey: () => "mission-control",
  },

  // Minesweeper is a utility game app without module permission.
  // Opens in a small floating window at its defaultSize, regardless of
  // whether the focused window is maximised.
  minesweeper: {
    id: "minesweeper",
    label: "Minesweeper",
    icon: <FontAwesomeIcon icon={faBomb} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-slate-600 via-gray-600 to-gray-700",
      iconColor: "text-white",
      coloredIconColor: "text-slate-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 328, height: 418 },
    openWindowed: true,
    render: () => <Minesweeper />,
  },

  codex: {
    id: "codex",
    label: "Codex",
    icon: <FontAwesomeIcon icon={faBook} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-slate-800 via-slate-900 to-slate-950",
      iconColor: "text-white",
      coloredIconColor: "text-cyan-400",
      shadow: "shadow-lg",
    },
    showInDock: true,
    defaultSize: { width: 1280, height: 960 },
    render: (params) => {
      if (params.taskKey) {
        return (
          <React.Suspense fallback={<LoadingPlaceholder text="Loading Codex…" />}>
            <CodexTaskPage taskKey={params.taskKey} missionId={params.missionId} />
          </React.Suspense>
        );
      }
      if (params.missionId) {
        const view =
          params.view === "network" ||
          params.view === "brief" ||
          params.view === "blueprint" ||
          params.view === "requirements"
            ? params.view
            : undefined;
        return (
          <React.Suspense fallback={<LoadingPlaceholder text="Loading Codex…" />}>
            <CodexMissionPage missionId={params.missionId} initialView={view} />
          </React.Suspense>
        );
      }
      if (params.agentId) {
        return (
          <React.Suspense fallback={<LoadingPlaceholder text="Loading Codex…" />}>
            <CodexAgentPage agentId={params.agentId} />
          </React.Suspense>
        );
      }
      return (
        <React.Suspense fallback={<LoadingPlaceholder text="Loading Codex…" />}>
          <CodexPage />
        </React.Suspense>
      );
    },
  },

  // FCA demo: the applicant firm's view of its authorisation application.
  "fca-portal": {
    id: "fca-portal",
    label: NAV_ONLY_APPS.fca_portal.label,
    icon: <FontAwesomeIcon icon={NAV_ONLY_APPS.fca_portal.icon} className={APP_ICON_CLASS} />,
    colorTheme: NAV_ONLY_APPS.fca_portal.colorTheme as AppDefinition["colorTheme"],
    showInDock: false,
    module: "home",
    defaultSize: NAV_ONLY_APPS.fca_portal.defaultSize,
    minSize: NAV_ONLY_APPS.fca_portal.minSize,
    render: () => (
      <React.Suspense fallback={<LoadingPlaceholder text="Loading portal…" />}>
        <FcaApplicantPortal />
      </React.Suspense>
    ),
  },

  "mission-kit": {
    id: "mission-kit",
    label: "Mission Kit",
    icon: <FontAwesomeIcon icon={faBook} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-700 via-slate-900 to-slate-950",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    openWindowed: true,
    defaultSize: { width: 1280, height: 960 },
    windowKey: () => "mission-kit",
    render: (params) => {
      if (params.kitSlug) {
        return (
          <React.Suspense fallback={<LoadingPlaceholder />}>
            <MissionKitRunbookPage slug={params.kitSlug} />
          </React.Suspense>
        );
      }
      const tab =
        params.tab === "framework" || params.tab === "blueprints" || params.tab === "kits"
          ? params.tab
          : undefined;
      return (
        <React.Suspense fallback={<LoadingPlaceholder />}>
          <MissionKitHub initialTab={tab} />
        </React.Suspense>
      );
    },
  },

  "mission-kit-blueprint": {
    id: "mission-kit-blueprint",
    label: "Mission Blueprint",
    icon: <FontAwesomeIcon icon={faBook} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-700 via-slate-900 to-slate-950",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    openWindowed: true,
    defaultSize: { width: 1280, height: 960 },
    windowKey: (params) =>
      params.missionId ? `mission-kit-blueprint:${params.missionId}` : null,
    render: (params) => {
      if (!params.missionId) return null;
      const view =
        params.view === "network" ||
        params.view === "brief" ||
        params.view === "blueprint" ||
        params.view === "requirements" ||
        params.view === "runbook" ||
        params.view === "automation"
          ? params.view
          : undefined;
      return (
        <React.Suspense fallback={<LoadingPlaceholder />}>
          <MissionBlueprintPage missionId={params.missionId} initialView={view} />
        </React.Suspense>
      );
    },
  },

  "readable-content": {
    id: "readable-content",
    label: "Reader",
    icon: <FontAwesomeIcon icon={faFiles} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-slate-700 via-slate-800 to-slate-900",
      iconColor: "text-white",
      coloredIconColor: "text-slate-300",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 720, height: 560 },
    minSize: { width: 420, height: 320 },
    openWindowed: true,
    render: (params) => <ReadableContentWindow contentId={params.contentId} />,
  },

  "csv-table-preview": {
    id: "csv-table-preview",
    label: "CSV Preview",
    icon: <FontAwesomeIcon icon={faTable} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-slate-700 via-slate-800 to-slate-900",
      iconColor: "text-white",
      coloredIconColor: "text-slate-300",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 720, height: 480 },
    minSize: { width: 420, height: 280 },
    openWindowed: true,
    render: (params) => <CsvTablePreviewWindow contentId={params.contentId} />,
  },

  /**
   * Ops → Triggers hub (/ops/triggers). Separate from `ops` so OS windows can open
   * this route without relying on the router Outlet (not mounted inside OsLayout).
   */
  triggers: {
    id: "triggers",
    label: "Triggers",
    icon: <FontAwesomeIcon icon={faBolt} className="text-xl" />,
    colorTheme: {
      type: "gradient",
      colors: "from-amber-500 via-orange-500 to-orange-600",
      iconColor: "text-white",
      coloredIconColor: "text-amber-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    module: "ops",
    defaultSize: { width: 1100, height: 760 },
    minSize: { width: 640, height: 480 },
    render: () => <OpsTriggers />,
  },

  // Individual worker window (different from workers directory).
  // Inherits the ``worker`` module gate so deep links can't bypass it.
  worker: {
    id: "worker",
    label: "Worker",
    icon: <FontAwesomeIcon icon={faUsers} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "solid",
      colors: "bg-slate-700 dark:bg-slate-800",
      iconColor: "text-slate-400",
      shadow: "shadow-lg",
      iconStyle: "colored",
    },
    showInDock: false,
    module: "worker",
    windowKey: (params) => (params.id ? `worker:${params.id}` : null),
    render: (params, _openWindow, isActive) => (
      <WorkerWindow
        workerId={params.id}
        conversationId={params.conversation ?? params.conversationId}
        isActive={isActive}
      />
    ),
  },

  // Worker creation dialog
  "worker-create": {
    id: "worker-create",
    label: "Create Worker",
    icon: <FontAwesomeIcon icon={faUsers} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-emerald-600 via-emerald-600 to-teal-700",
      iconColor: "text-white",
      coloredIconColor: "text-emerald-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 1000, height: 700 },
    render: () => <WorkerCreateRoute />,
  },

  // Individual document window. Inherits the ``documents`` module gate.
  document: {
    id: "document",
    label: "Document",
    icon: <FontAwesomeIcon icon={faFiles} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "solid",
      colors: "bg-slate-700 dark:bg-slate-800",
      iconColor: "text-cyan-400",
      shadow: "shadow-lg",
      iconStyle: "colored",
    },
    showInDock: false,
    module: "documents",
    defaultSize: { width: 1000, height: 700 },
    render: (params) => <DocumentWindow documentId={params.id} />,
  },

  // Multiple portfolios window (used by workers). Inherits the ``portfolio`` module gate.
  portfolios: {
    id: "portfolios",
    label: "Portfolios",
    icon: <FontAwesomeIcon icon={faBriefcase} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-indigo-600 to-purple-700",
      coloredIconColor: "text-indigo-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    showInDock: false,
    module: "portfolio",
    defaultSize: { width: 1000, height: 700 },
    render: (params) => (
      <PortfoliosWindow
        entityId={params.entityId || ""}
        entityType={params.entityType || "worker"}
      />
    ),
  },

  // ============================================================================
  // Apps with Complex Render Functions (derived from MODULE_REGISTRY + custom render)
  // ============================================================================

  // Workers directory with complex render function
  workers: {
    ...createAppFromModule("worker", (_params, openWindow) => {
      const useChatLayout = _params?.layout !== "directory";

      if (useChatLayout) {
        return <WorkerChatLayout openWindow={openWindow} />;
      }

      return (
        <WorkerDirectory
          onCreate={() =>
            openWindow({
              appId: "worker-create",
              title: "Create Worker",
              params: {},
            })
          }
          onWorkerClick={(worker) =>
            openWindow({
              appId: "worker",
              title: worker.name,
              params: { id: worker._id },
            })
          }
        />
      );
    }),
    id: "workers", // Override id since module's osAppId is "workers"
    windowKey: (params) => (params.layout === "directory" ? null : "workers"),
  },

  // Pods directory with complex render function
  pods: {
    ...createAppFromModule("pod", (params, openWindow) => {
      if (params?.id) {
        return <PodRoute templateId={params.id} />;
      }

      return (
        <PodDirectory
          onSelectPod={(pod) =>
            openWindow({
              appId: "pods",
              title: pod.name,
              params: { id: pod._id },
            })
          }
        />
      );
    }),
    id: "pods", // Override id since module's osAppId is "pods"
    windowKey: (params) => (params.id ? `pods:${params.id}` : null),
  },

  // Missions: list of active client missions (Agentic v2).
  missions: {
    ...createAppFromModule("missions", (params) => (
      <MissionsListRoute focusKey={params.focus ?? null} />
    )),
    windowKey: () => "missions",
  },

  // Clients: household knowledge centre.
  clients: {
    ...createAppFromModule("clients", () => <ClientsListRoute />),
    windowKey: () => "clients",
  },

  // Single-client detail window (opened from the clients list).
  // Child window — inherits the ``clients`` module gate so deep links
  // can't bypass the parent app's permission.
  client: {
    id: "client",
    label: "Client",
    icon: <FontAwesomeIcon icon={faRocket} className={APP_ICON_CLASS} />,
    defaultSize: { width: 1100, height: 800 },
    colorTheme: {
      type: "gradient",
      colors: "from-sky-600 via-cyan-600 to-teal-700",
      iconColor: "text-white",
      coloredIconColor: "text-sky-400",
    },
    module: "clients",
    windowKey: (params) =>
      params.faClientId ? `client:${params.faClientId}` : null,
    render: (params) => (
      <ClientDetailRoute faClientId={params.faClientId ?? ""} />
    ),
  },

  // Leads: all-leads list (own module gate).
  leads: {
    ...createAppFromModule("leads", () => <LeadsListRoute />),
    windowKey: () => "leads",
  },

  // Single-lead record window (opened from the leads list or review close).
  // Child window — inherits the ``leads`` module gate so deep links
  // can't bypass the parent app's permission.
  lead: {
    id: "lead",
    label: "Lead",
    icon: <FontAwesomeIcon icon={faRocket} className={APP_ICON_CLASS} />,
    defaultSize: { width: 1100, height: 800 },
    colorTheme: {
      type: "gradient",
      colors: "from-teal-600 via-emerald-600 to-green-700",
      iconColor: "text-white",
      coloredIconColor: "text-teal-400",
    },
    module: "leads",
    windowKey: (params) =>
      params.faClientId ? `lead:${params.faClientId}` : null,
    render: (params) => (
      <LeadDetailRoute faClientId={params.faClientId ?? ""} />
    ),
  },

  // Artifacts: agentic v2 documents (short letters etc.) — list view.
  // Clicking a row opens a dedicated ``artifact`` window.
  artifacts: {
    ...createAppFromModule("artifacts", (_params, openWindow) => (
      <ArtifactsApp
        onOpenArtifact={(artifactId, title) =>
          void openArtifactById(artifactId, openWindow, { mode: "view", title })
        }
        onOpenDocumentTemplate={(templateKey, title) =>
          openWindow({
            appId: "document-template",
            title,
            params: { templateKey },
          })
        }
        onOpenFormSchema={(schemaKey, title) =>
          openWindow({
            appId: "form-schema",
            title,
            params: { schemaKey },
          })
        }
        onOpenChecklistSchema={(schemaKey, title) =>
          openWindow({
            appId: "checklist-schema",
            title,
            params: { schemaKey },
          })
        }
      />
    )),
    windowKey: () => "artifacts",
  },

  // Artifact: single-document editor or review window.
  // Deep-linkable at /artifacts/:id (see urlMapping).
  artifact: {
    id: "artifact",
    label: "Artefact",
    icon: <FontAwesomeIcon icon={faFiles} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-cyan-600 via-sky-600 to-blue-700",
      iconColor: "text-white",
      coloredIconColor: "text-cyan-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 1100, height: 800 },
    module: "artifacts",
    windowKey: (params) => (params.id ? `artifact:${params.id}` : null),
    render: (params) => (params.id ? <ArtifactWindow /> : null),
  },

  // Document template editor window. Inherits the ``artifacts`` gate.
  "document-template": {
    id: "document-template",
    label: "Document template",
    icon: <FontAwesomeIcon icon={faFiles} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-violet-600 via-fuchsia-600 to-purple-700",
      iconColor: "text-white",
      coloredIconColor: "text-violet-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 1200, height: 800 },
    module: "artifacts",
    windowKey: (params) =>
      params.templateKey ? `document-template:${params.templateKey}` : null,
    render: (params) =>
      params.templateKey ? (
        <DocumentTemplateWindow templateKey={params.templateKey} />
      ) : null,
  },

  // Form schema editor window. Inherits the ``artifacts`` gate.
  "form-schema": {
    id: "form-schema",
    label: "Form template",
    icon: <FontAwesomeIcon icon={faFiles} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-amber-500 via-orange-500 to-rose-500",
      iconColor: "text-white",
      coloredIconColor: "text-amber-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 1100, height: 800 },
    module: "artifacts",
    windowKey: (params) =>
      params.schemaKey ? `form-schema:${params.schemaKey}` : null,
    render: (params) =>
      params.schemaKey ? (
        <FormSchemaWindow schemaKey={params.schemaKey} />
      ) : null,
  },

  // Checklist schema editor window. Inherits the ``artifacts`` gate.
  "checklist-schema": {
    id: "checklist-schema",
    label: "Checklist template",
    icon: <FontAwesomeIcon icon={faListCheck} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-emerald-600 via-teal-600 to-cyan-700",
      iconColor: "text-white",
      coloredIconColor: "text-emerald-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 1100, height: 800 },
    module: "artifacts",
    windowKey: (params) =>
      params.schemaKey ? `checklist-schema:${params.schemaKey}` : null,
    render: (params) =>
      params.schemaKey ? (
        <ChecklistSchemaWindow schemaKey={params.schemaKey} />
      ) : null,
  },

  // Mission: individual mission detail view (opened from the missions list).
  // Inherits the ``missions`` gate.
  mission: {
    id: "mission",
    label: "Mission",
    icon: <FontAwesomeIcon icon={faRocket} className={APP_ICON_CLASS} />,
    defaultSize: { width: 1000, height: 700 },
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-violet-600 to-purple-700",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-400",
    },
    module: "missions",
    windowKey: (params) =>
      params.missionId ? `mission:${params.missionId}` : null,
    render: (params) => (
      <MissionDetailRoute missionId={params.missionId ?? ""} />
    ),
  },

  // Pod Instance window (individual pod instance view) - legacy complex view
  "pod-instance": {
    id: "pod-instance",
    label: "Pod Instance",
    icon: <FontAwesomeIcon icon={faRocket} className={APP_ICON_CLASS} />,
    defaultSize: { width: 1000, height: 700 },
    colorTheme: {
      type: "gradient",
      colors: "from-orange-600 via-red-600 to-red-700",
      iconColor: "text-white",
      coloredIconColor: "text-orange-400",
    },
    windowKey: (params) =>
      params.templateId && params.instanceId
        ? `pod-instance:${params.templateId}:${params.instanceId}`
        : null,
    render: (params) => (
      <PodInstance id={params.instanceId} template={params.templateId ?? ""} />
    ),
  },

  // Space Instance window - simplified chat-first view
  "space-instance": {
    id: "space-instance",
    label: "Space",
    icon: <FontAwesomeIcon icon={faComments} className="text-xl" />,
    defaultSize: { width: 900, height: 650 },
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-violet-600 to-purple-700",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-400",
    },
    isSingleton: false,
    render: (params) => (
      <SpaceInstance id={params.instanceId} templateId={params.templateId} />
    ),
  },

  // Space Template window - simplified header + kanban view
  "space-template": {
    id: "space-template",
    label: "Workspace",
    icon: <FontAwesomeIcon icon={faBriefcase} className="text-xl" />,
    defaultSize: { width: 1100, height: 700 },
    colorTheme: {
      type: "gradient",
      colors: "from-slate-600 via-slate-700 to-slate-800",
      iconColor: "text-white",
      coloredIconColor: "text-slate-400",
    },
    isSingleton: false,
    render: (params) => (
      <SpaceTemplate templateId={params.id} />
    ),
  },

  // Documents: cloud docs open in OS window; PDFs etc. download via Documents route + OsContext
  documents: {
    ...createAppFromModule("documents", () => <Documents />),
  },

  // Portfolio window with params
  portfolio: {
    ...createAppFromModule("portfolio", (params) => <PortfolioWindow params={params} />),
  },

  // Product window with params
  product: {
    ...createAppFromModule("product", (params) => <ProductWindow params={params} />),
  },

  // Report Builder with params
  "report-builder": {
    ...createAppFromModule("report_builder", (params) => (
      <ReportBuilderWindow params={params} />
    )),
  },

  // ============================================================================
  // Simple Apps (derived from MODULE_REGISTRY with simple render functions)
  // ============================================================================

  dashboard: createAppFromModule("dashboard", () => (
    <React.Suspense fallback={<LoadingPlaceholder />}>
      <Dashboard />
    </React.Suspense>
  )),
  home: createAppFromModule("home", () => <HomeWindowContent />),
  reports: createAppFromModule("reports", (_params, openWindow) => (
    <Reports osOpenWindow={openWindow} />
  )),
  meetings: {
    ...createAppFromModule("meetings", () => <Meetings />),
    windowScope: "workspace",
    windowKey: () => "meetings",
  },
  // Dedicated full-screen workspace for a single meeting. Opened from the
  // calendar drawer via "Open full view"; deep-linked at /meetings/:id?view=full.
  meeting: {
    ...createAppFromModule("meetings", (params) =>
      params.meetingId ? (
        <MeetingWindow meetingId={params.meetingId} />
      ) : null,
    ),
    id: "meeting",
    label: "Meeting",
    showInDock: false,
    defaultSize: { width: 1280, height: 860 },
    minSize: { width: 760, height: 520 },
    windowKey: (params) =>
      params.meetingId ? `meeting:${params.meetingId}` : null,
  },
  "task-review": {
    id: "task-review",
    label: "Review",
    icon: <FontAwesomeIcon icon={faListCheck} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-violet-600 to-purple-700",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-300",
      shadow: "shadow-lg",
    },
    showInDock: false,
    module: "home",
    defaultSize: { width: 1200, height: 860 },
    minSize: { width: 900, height: 600 },
    demoteMaximizedOnFocus: true,
    windowKey: (params) =>
      params.taskId ? `task-review:${params.taskId}` : null,
    render: (params) =>
      params.taskId ? (
        <React.Suspense
          fallback={
            <div className="flex h-full min-h-0 items-center justify-center p-6">
              <LoadingPlaceholder pattern="card" count={2} text="Loading review…" />
            </div>
          }
        >
          <TaskReviewWorkspace
            key={params.taskId}
            taskId={params.taskId}
            initialFocus={params.focus === "letter" ? "letter" : "case"}
            initialMode={params.mode === "progress" ? "progress" : "review"}
          />
        </React.Suspense>
      ) : null,
  },
  screener: createAppFromModule("screener", (params) => (
    <ScreenerWindowContent
      configId={params.configId}
      resourceId={params.resourceId}
      resourceType={
        params.resourceType === "portfolio" ||
        params.resourceType === "product" ||
        params.resourceType === "index"
          ? params.resourceType
          : null
      }
    />
  )),
  playground: createAppFromModule("playground", (params) => (
    <PlaygroundWindowContent
      assets={params.assets}
      layout={params.layout}
      from={params.from}
      period={params.period}
      frequency={params.frequency}
      display={params.display}
      start_date={params.start_date}
      end_date={params.end_date}
    />
  )),
  ops: createAppFromModule("ops", () => <Ops />),
  settings: {
    ...createAppFromModule("settings", () => <Settings />),
    isSingleton: true,
  },

  // Ops sub-apps - each ops tool opens in its own window
  "ops-checklists": {
    id: "ops-checklists",
    label: "Ops Checklists",
    icon: <FontAwesomeIcon icon={faListCheck} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    windowKey: (params) =>
      `ops-checklists:${params.runId ?? ""}:${params.checklistView ?? ""}`,
    render: (params) => <OsChecklistsWindowContent params={params} />,
  },
  "ops-checker": {
    id: "ops-checker",
    label: "Ops Checker",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsChecker />,
  },
  "ops-system-jobs": {
    id: "ops-system-jobs",
    label: "System Jobs",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsSystemJobs />,
  },
  "ops-task-manager": {
    id: "ops-task-manager",
    label: "Task Manager",
    icon: <FontAwesomeIcon icon={faRocket} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsTaskManager />,
  },
  "ops-mission-control-ops": {
    id: "ops-mission-control-ops",
    label: "Mission Control Ops",
    icon: <FontAwesomeIcon icon={faDiagramSubtask} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1100, height: 800 },
    module: "ops",
    render: () => <OpsMissionControlOps />,
  },
  "ops-etl-runs": {
    id: "ops-etl-runs",
    label: "ETL Runs",
    icon: <FontAwesomeIcon icon={faWrench} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsEtlRuns />,
  },
  "ops-db-versions": {
    id: "ops-db-versions",
    label: "DB Versions",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsDbVersions />,
  },
  "ops-worker-templates": {
    id: "ops-worker-templates",
    label: "Worker Templates",
    icon: <FontAwesomeIcon icon={faFolderOpen} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsWorkerTemplates />,
  },
  "ops-etl-audit": {
    id: "ops-etl-audit",
    label: "ETL Audit",
    icon: <FontAwesomeIcon icon={faListCheck} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsEtlAudit />,
  },
  "ops-snapshots": {
    id: "ops-snapshots",
    label: "Snapshots",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsSnapshots />,
  },
  "ops-access-permissions": {
    id: "ops-access-permissions",
    label: "Access & Permissions",
    icon: <FontAwesomeIcon icon={faUsers} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsAccessPermissions />,
  },
  "ops-refinitiv": {
    id: "ops-refinitiv",
    label: "Refinitiv",
    icon: <FontAwesomeIcon icon={faChartLine} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsRefinitiv />,
  },
  "ops-msci": {
    id: "ops-msci",
    label: "MSCI",
    icon: <FontAwesomeIcon icon={faGlobe} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsMsci />,
  },
  "ops-seccl": {
    id: "ops-seccl",
    label: "SECCL",
    icon: <FontAwesomeIcon icon={faChartLine} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsSeccl />,
  },
  "ops-guardians": {
    id: "ops-guardians",
    label: "The Guardians",
    icon: <FontAwesomeIcon icon={faShieldHeart} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    windowKey: (params) =>
      params.subRoute === "tickets" ? "ops-guardians:tickets" : "ops-guardians:main",
    render: (params) =>
      params.subRoute === "tickets" ? (
        <OpsGuardiansTickets initialTicketRef={params.ticket} />
      ) : (
        <GuardiansApp />
      ),
  },
  "ops-guardians-conversation": {
    id: "ops-guardians-conversation",
    label: "Conversation",
    icon: <FontAwesomeIcon icon={faComments} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    showInDock: false,
    openWindowed: true,
    isResizable: true,
    defaultSize: { width: 480, height: 760 },
    minSize: { width: 400, height: 480 },
    module: "ops",
    windowKey: (params) =>
      `${params.mission ?? ""}:${params.task ?? ""}`,
    render: (params) => (
      <GuardiansConversationWindow
        missionId={params.mission ? String(params.mission) : undefined}
        taskId={params.task ? String(params.task) : undefined}
        focus={params.focus ? String(params.focus) : undefined}
      />
    ),
  },
  "ops-cache-management": {
    id: "ops-cache-management",
    label: "Cache Management",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsCacheManagement />,
  },
  "ops-data-sentinel": {
    id: "ops-data-sentinel",
    label: "Data Sentinel",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsDataSentinel />,
  },
  "ops-temporal-rag": {
    id: "ops-temporal-rag",
    label: "Temporal RAG POC",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsTemporalRagPoc />,
  },
  "ops-data-gathering": {
    id: "ops-data-gathering",
    label: "Data Gathering",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    openWindowed: true,
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsDataGathering />,
  },
  "ops-dg-case": {
    id: "ops-dg-case",
    label: "DG Case",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    showInDock: false,
    openWindowed: true,
    defaultSize: { width: 1000, height: 800 },
    minSize: { width: 640, height: 480 },
    module: "ops",
    render: (params) => (
      <React.Suspense fallback={null}>
        {params.faClientId ? (
          <DataGatheringCaseWindow
            faClientId={String(params.faClientId)}
            displayName={params.displayName ? String(params.displayName) : undefined}
          />
        ) : (
          <OpsDataGathering />
        )}
      </React.Suspense>
    ),
  },
  "dg-monitoring-task": {
    id: "dg-monitoring-task",
    label: "DG Monitoring",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    showInDock: false,
    openWindowed: true,
    defaultSize: { width: 480, height: 800 },
    minSize: { width: 400, height: 400 },
    module: "ops",
    render: (params) => (
      <React.Suspense fallback={null}>
        {params.taskId ? (
          <DgMonitoringTaskWindow taskId={params.taskId} />
        ) : (
          <OpsDataGathering />
        )}
      </React.Suspense>
    ),
  },
  "ops-feature-flags": {
    id: "ops-feature-flags",
    label: "Feature Flags",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsFeatureFlags />,
  },
  "ops-external-data": {
    id: "ops-external-data",
    label: "External Data",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsExternalData />,
  },
  "ops-news-ingest": {
    id: "ops-news-ingest",
    label: "News Ingestion",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1000, height: 700 },
    module: "ops",
    render: () => <OpsNewsIngest />,
  },
  "ops-daily-ihf-ingest": {
    id: "ops-daily-ihf-ingest",
    label: "Daily IHF Ingestion",
    icon: <FontAwesomeIcon icon={faTable} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1100, height: 780 },
    module: "ops",
    render: () => <OpsAutomaticIngestionsDailyIhf />,
  },
  "ops-automatic-ingestions": {
    id: "ops-automatic-ingestions",
    label: "Automatic Ingestions",
    icon: <FontAwesomeIcon icon={faRobot} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 960, height: 640 },
    module: "ops",
    render: (params) => {
      if (params.pipeline === "daily-ihf") {
        return <OpsAutomaticIngestionsDailyIhf />;
      }
      if (params.pipeline === "daily-ihf-cash-fund-rec") {
        return <OpsAutomaticIngestionsCashFundRec />;
      }
      return <OpsAutomaticIngestions />;
    },
  },
  "ops-magic-upload": {
    id: "ops-magic-upload",
    label: "Magic Upload",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsMagicUpload />,
  },
  "ops-agent-testing": {
    id: "ops-agent-testing",
    label: "Agent A/B Testing",
    icon: <FontAwesomeIcon icon={faFlask} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: (params) => {
      if (params.abTestId) {
        return (
          <React.Suspense fallback={null}>
            <OpsAgentTestingAbTest />
          </React.Suspense>
        );
      }
      if (params.view === "configure") {
        return (
          <React.Suspense fallback={null}>
            <OpsAgentTestingConfigure />
          </React.Suspense>
        );
      }
      return (
        <React.Suspense fallback={null}>
          <OpsAgentTesting />
        </React.Suspense>
      );
    },
  },
  "ops-agent-review": {
    id: "ops-agent-review",
    label: "Agent Review",
    icon: <FontAwesomeIcon icon={faFlask} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: (params) => (
      <React.Suspense fallback={null}>
        <OsAgentReviewWindowContent params={params} />
      </React.Suspense>
    ),
  },
  "ops-ace-task-review": {
    id: "ops-ace-task-review",
    label: "Ace Task Review",
    icon: <FontAwesomeIcon icon={faRobot} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: (params) => (
      <React.Suspense fallback={null}>
        <OsAceTaskReviewWindowContent params={params} />
      </React.Suspense>
    ),
  },
  "ops-curo": {
    id: "ops-curo",
    label: "Curo Connect",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsCuro />,
  },
  "ops-meeting-connectors": {
    id: "ops-meeting-connectors",
    label: "Meeting connectors",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsMeetingConnectors />,
  },
  "ops-progeny": {
    id: "ops-progeny",
    label: "Progeny Integration",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    windowKey: (params) => {
      const subRoute = params.subRoute ?? "";
      if (subRoute === "review" && params.reviewId) {
        return `ops-progeny:review:${params.reviewId}`;
      }
      return subRoute ? `ops-progeny:${subRoute}` : "ops-progeny:main";
    },
    render: (params) => {
      if (params.subRoute === "webhooks") return <OpsProgenyWebhooks />;
      if (params.subRoute === "processing") return <OpsProgenyProcessing />;
      if (params.subRoute === "sam-testing") return <OpsProgenySamTesting />;
      if (params.subRoute === "review" && params.reviewId) {
        return <OsProgenyReviewWindowBridge reviewId={params.reviewId} />;
      }
      return <OpsProgeny />;
    },
  },
  "ops-case-checking": {
    id: "ops-case-checking",
    label: "Case Checking Ops",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: (params) => (
      <React.Suspense fallback={null}>
        <CaseCheckingOpsApp initialView={params.view} />
      </React.Suspense>
    ),
  },
  "ops-progeny-file-checking": {
    id: "ops-progeny-file-checking",
    label: "Progeny File Checking",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsProgenyFileChecking />,
  },
  "ops-progeny-advisor": {
    id: "ops-progeny-advisor",
    label: "Progeny Advisor Ops",
    icon: <FontAwesomeIcon icon={faServer} className={APP_ICON_CLASS} />,
    colorTheme: MODULE_REGISTRY.ops?.colorTheme as AppDefinition["colorTheme"],
    defaultSize: { width: 1200, height: 800 },
    module: "ops",
    render: () => <OpsProgenyAdvisor />,
  },
  "scenario-tracker": {
    ...createAppFromModule("scenario_tracker", (params) => {
      if (params.id) {
        return (
          <React.Suspense fallback={null}>
            <ScenariosAnalysis key={params.id} id={params.id} />
          </React.Suspense>
        );
      }
      return <ScenarioTrackerWindow initialTab="exec-summary" fixedTab />;
    }),
    label: "Scenario Key Insights",
    colorTheme: SCENARIO_TRACKER_COLOR_THEMES["exec-summary"],
    showInDock: true,
  },
  "scenario-tracker-newsflow": {
    ...createScenarioTrackerTabApp(
      "scenario-tracker-newsflow",
      "Scenario Newsflow",
      "news-flow",
      faFiles
    ),
    isSingleton: true,
  },
  "scenario-tracker-directionality": createScenarioTrackerTabApp(
    "scenario-tracker-directionality",
    "Scenario Directionality",
    "master-analysis",
    faChartPie
  ),
  "scenario-tracker-heatmap": createScenarioTrackerTabApp(
    "scenario-tracker-heatmap",
    "Scenario Heatmap",
    "summary",
    faTable
  ),
  "scenario-tracker-deep-dive": createScenarioTrackerTabApp(
    "scenario-tracker-deep-dive",
    "Scenario Deep Dive",
    "deep-dive",
    faFilters
  ),
  "scenario-tracker-metric-projections": createScenarioTrackerTabApp(
    "scenario-tracker-metric-projections",
    "Scenario Metric Projections",
    "metric-projections",
    faChartLine
  ),
  "scenario-metric-detail": {
    id: "scenario-metric-detail",
    label: "Scenario Metric Detail",
    icon: <FontAwesomeIcon icon={faChartLine} className="text-xl" />,
    colorTheme: {
      type: "gradient",
      colors: "from-slate-600 via-blue-700 to-indigo-800",
      iconColor: "text-white",
      coloredIconColor: "text-blue-300",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 820, height: 720 },
    minSize: { width: 520, height: 420 },
    module: "scenario_tracker",
    windowKey: () => null,
    render: (params) => <ScenarioMetricDetailWindow {...params} />,
  },
  "scenario-article-detail": {
    id: "scenario-article-detail",
    label: "Scenario Article Detail",
    icon: <FontAwesomeIcon icon={faFiles} className="text-xl" />,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-slate-700 to-slate-900",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-300",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 640, height: 720 },
    minSize: { width: 640, height: 720 },
    isResizable: false,
    module: "scenario_tracker",
    windowKey: () => null,
    render: (params) => <ScenarioArticleDetailWindow {...params} />,
  },
  "what-if": createAppFromModule("what_if", (params) => {
    if (params.portfolioId && params.scenarioId) {
      return (
        <WhatIfScenarioWindowContent
          portfolioId={params.portfolioId}
          scenarioId={params.scenarioId}
        />
      );
    }
    // If portfolioId is provided, show the portfolio analysis page
    if (params.portfolioId) {
      return <WhatIfPortfolioWindowContent portfolioId={params.portfolioId} />;
    }
    // Otherwise show the main what-if page
    return <WhatIf />;
  }),
  "cash-flow": createAppFromModule("cash_flow", () => <CashFlow />),
  "add-products": createAppFromModule("add_products", (params) => (
    <AddProductsApp
      initialMode={params.mode === "create" ? "create" : params.mode === "search" ? "search" : undefined}
      initialAssetClass={
        params.assetClass === "fund" || params.assetClass === "equity" || params.assetClass === "bond"
          ? params.assetClass
          : "all"
      }
    />
  )),
  "fund-processor": createAppFromModule("fund_processor", () => <FundProcessorRoute />),
  "equity-processor": createAppFromModule("equity_processor", () => <EquityProcessorRoute />),
  "bond-processor": createAppFromModule("bond_processor", () => <BondProcessorRoute />),
  "custom-products": createAppFromModule("custom_products", () => <CustomProductsRoute />),
  "trade-blotter": createAppFromModule("trade_blotter", () => <TradeBlotter />),
  "portfolio-builder": createAppFromModule("portfolio_builder", () => <PortfolioBuilder />),
  "portfolio-builder-duplicate": {
    id: "portfolio-builder-duplicate",
    label: "Duplicate Portfolio",
    icon: <FontAwesomeIcon icon={faBriefcase} className={APP_ICON_CLASS} />,
    colorTheme: {
      type: "gradient",
      colors: "from-purple-700 via-purple-700 to-indigo-800",
      iconColor: "text-white",
      coloredIconColor: "text-purple-400",
      shadow: "shadow-lg",
    },
    showInDock: false,
    defaultSize: { width: 900, height: 700 },
    minSize: { width: 640, height: 480 },
    module: "portfolio_builder",
    render: () => <PortfolioDuplicateWindow />,
  },
  "data-hub": createAppFromModule("data_ingest", () => <DataHub />),
  report: {
    id: "report",
    label: "Report",
    icon: <FontAwesomeIcon icon={faFiles} />,
    defaultSize: { width: 1200, height: 800 },
    render: (params) => (
      <OsEmbeddedRouteFrame basePath="/report" params={params} />
    ),
  },
  "pdf-viewer": {
    id: "pdf-viewer",
    label: "Report PDF",
    icon: <FontAwesomeIcon icon={faFiles} />,
    defaultSize: { width: 1000, height: 800 },
    render: (params) => (
      <OsEmbeddedRouteFrame basePath="/pdf" params={params} />
    ),
  },
  "benchmark-builder": createAppFromModule("benchmark_builder", () => <BenchmarkBuilder />),
  "monte-carlo": createAppFromModule("monte_carlo", () => <MonteCarlo />),
  cmas: createAppFromModule("cmas", () => <CMAs />),
  optimiser: createAppFromModule("optimiser", () => <Optimiser />),
  "factor-analysis": createAppFromModule("factor_analysis", () => <FactorAnalysis />),
  "portfolio-lab": createAppFromModule("portfolio_lab", () => <PortfolioLab />),
  "advisor-dashboard": createAppFromModule("advisor_dashboard", () => <AdvisorDashboard />),
  "pulse-v2": createAppFromModule("pulse_v2", (params) => (
    <PulseDashboard initialPackId={params.packId} />
  )),
  rebalancing: createAppFromModule("rebalancing", () => <RebalancingContainer />),
  twr: createAppFromModule("twr", () => <TWRContainer />),
  irr: createAppFromModule("irr", () => <IRRContainer />),
  calculators: createAppFromModule("calculators", (params) => (
    <React.Suspense fallback={<LoadingPlaceholder text="Loading calculators…" />}>
      <CalculatorsWindowContent
        calculatorId={params.calculatorId}
        faClientId={params.faClientId}
      />
    </React.Suspense>
  )),
  "case-checking": {
    ...createAppFromModule("case_checking", (params) =>
      params.view === "rules" ? (
        <CaseCheckingRules />
      ) : (
        <CaseCheckingView />
      ),
    ),
    windowScope: "workspace",
    windowKey: (params) => {
      if (params.view === "rules") {
        return "case-checking:rules";
      }
      if (params.caseId) {
        return `case-checking:${params.caseId}`;
      }
      return "case-checking:list";
    },
  },
  disclosures: {
    ...createAppFromModule("disclosures", () => <DisclosuresView />),
    windowScope: "workspace",
    windowKey: (params) => params.caseId ?? "disclosures:list",
  },
};

registerAppRegistry(APP_REGISTRY);

/**
 * Ordered list of apps to show in the dock by default (permission-dependent)
 * Order: Dashboard, Screener, Playground, Cash Flow, Reports, …
 */
export const DOCK_APPS = [
  "home",
  "dashboard",
  "mission-control",
  "missions",
  "clients",
  "leads",
  "artifacts",
  "pulse-v2",
  "screener",
  "playground",
  "scenario-tracker",
  "scenario-tracker-newsflow",
  "scenario-tracker-directionality",
  "scenario-tracker-heatmap",
  "scenario-tracker-deep-dive",
  "scenario-tracker-metric-projections",
  "cash-flow",
  "add-products",
  "reports",
  "report-builder",
  "documents",
  "pods",
  "workers",
];

export type FilterAppsAccessContext = {
  user?: Parameters<typeof canAccessCodex>[0];
  modulePermissions?: Parameters<typeof canAccessCodex>[1];
};

/**
 * Filters apps based on user permissions.
 *
 * @param apps - Array of app IDs to filter
 * @param hasPermission - Permission check function from useModulePermissions
 * @returns Array of app IDs that the user has permission to access
 *
 * @example
 * const { hasPermission } = useModulePermissions();
 * const allowedAppIds = filterAppsByPermission(APPS_POPOVER_IDS, hasPermission);
 */
export function filterAppsByPermission(
  apps: string[],
  hasPermission: (module: string) => boolean,
  access?: FilterAppsAccessContext,
): string[] {
  return apps.filter((appId) => {
    const app = APP_REGISTRY[appId];
    if (!app) return false;

    if (appId === "codex") {
      return canAccessCodex(access?.user, access?.modulePermissions);
    }

    // If app has no module defined, allow it (e.g., utility apps like minesweeper)
    if (!app.module) return true;

    // Check if user has permission for this app's module
    return hasPermission(app.module);
  });
}

/**
 * Checks if a user has permission to access a window's content.
 * Returns true if the app has no module requirement or if the user
 * has the required permission.
 *
 * @param appId - The app ID from the window
 * @param hasPermission - Function to check module permissions
 * @returns True if the user can access the window content
 */
export function canAccessWindow(
  appId: string,
  hasPermission: (module: string) => boolean,
  user?: { company_id?: string; role?: string; permissions?: string[]; email?: string } | null,
  modulePermissions?: Pick<ModulePermissions, "role" | "permissions"> | null,
): boolean {
  const app = APP_REGISTRY[appId];

  if (!app) {
    return false;
  }

  if (appId === "ops-seccl") {
    return hasPermission("ops") && canAccessAniPlatformAdminOps(user);
  }

  if (appId === "ops-guardians") {
    return canAccessGuardians(hasPermission, user);
  }

  if (appId === "ops-guardians-conversation") {
    return canAccessGuardians(hasPermission, user);
  }

  if (appId === "mission-kit" || appId === "mission-kit-blueprint") {
    return canAccessMissionKitPlatform(user, modulePermissions);
  }

  if (appId === "codex") {
    return canAccessCodex(user, modulePermissions);
  }

  if (!app.module) {
    return true;
  }

  return hasPermission(app.module);
}
